import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Camara } from '../../camaraManager/model/Camara.ts';
import { updateCamaraById, createCamara, markExternalChipICCID, clearExternalChipICCID, updateCamaraBillingType, getCamaraBillingType } from '../../camaraManager/api/camaraManager.api.js';
import { fetchRemainingImages } from '../api/migrate.api.js';
import { getObras } from '../../obras/api/obras.api.js';
import { Obra } from '../../obras/types/Obra';
import { getSlotQuotaMessage } from '../../utils/slotQuota.js';
import { ORIENTATION_DEGREES, ORIENTATION_LABELS } from '../../utils/cameraOrientation.js';

interface SaveOrEditCamarasModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseCancel: () => void;
  camara?: Camara;
}

const SaveOrEditCamaras: React.FC<SaveOrEditCamarasModalProps> = ({ isOpen, onClose, onCloseCancel, camara }) => {
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const [formData, setFormData] = useState<Camara>({
    id: "",
    feedID: "",
    customerId: "",
    responsibleUser: "",
    active: true,
    permission: "",
    visible: true,
    Name: "",
    identifier: "",
    paymentIntent: "",
    status: "",
    chipICCID: "",
    camaraUID: "",
    sourceType: "FTPS3", // por defecto: fotos por FTP, que la IA procesa
    orientation: "",
    coordinates: { lat: 0, lng: 0 },
    feedDays: {
      daysLeft: 0,
      totalDays: 0,
      createdAt: new Date(),
    },
    pirAnalytics: true, // todas las cámaras se analizan con IA
    ropActive: false,
    billingType: "paquete_dias",
    renewalDay: null,
    obra: null,
  });
  const [savingBillingType, setSavingBillingType] = useState(false);
  const [obras, setObras] = useState<Obra[]>([]);

  useEffect(() => {
    console.log("Camara prop changed:", camara);
    if (camara) {
      setFormData({
        ...camara,
        ropActive: camara.ropActive ?? false,
        billingType: camara.billingType || "paquete_dias",
        renewalDay: camara.renewalDay ?? null,
        obra: camara.obra ?? null,
        feedDays: {
          ...camara.feedDays,
          createdAt: new Date(camara.feedDays.createdAt),
        },
      });
    } else {
      setFormData({
        id: "",
        feedID: "",
        customerId: "",
        Name: "",
        identifier: "",
        paymentIntent: "",
        status: "",
        permission: "",
        chipICCID: "",
        camaraUID: "",
        responsibleUser: "",
        active: true,
        visible: true,
        orientation: "",
        coordinates: { lat: 0, lng: 0 },
        feedDays: {
          daysLeft: 0,
          totalDays: 0,
          createdAt: new Date(),
        },
        pirAnalytics: true, // todas las cámaras se analizan con IA
        ropActive: false,
        billingType: "paquete_dias",
        renewalDay: null,
        obra: null,
      });
    }
  }, [camara]);

  // Obras del cliente dueño de la cámara, para el selector de asignación.
  useEffect(() => {
    const customerId = camara?.customerId;
    if (!customerId) {
      setObras([]);
      return;
    }

    let cancelled = false;
    getObras(customerId)
      .then((data) => {
        if (!cancelled) setObras(data || []);
      })
      .catch((error) => {
        console.error("Error al obtener las obras del cliente:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [camara?.customerId]);

  // El back viejo (de donde viene `camara`) no conoce billingType/renewalDay —
  // viven solo en el webback nuevo, así que se piden acá aparte por identifier.
  useEffect(() => {
    if (!camara?.identifier) return;

    let cancelled = false;
    getCamaraBillingType(camara.identifier)
      .then((data) => {
        if (cancelled) return;
        setFormData(prev => ({
          ...prev,
          billingType: data.billingType || "paquete_dias",
          renewalDay: data.renewalDay ?? null,
        }));
      })
      .catch((error) => {
        console.error("Error al obtener el tipo de licencia de la cámara:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [camara?.identifier]);

 const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
  const { name, value, type } = e.target;

  let inputValue: any;
  if (type === "checkbox") inputValue = (e.target as HTMLInputElement).checked;
  else if (type === "number") inputValue = Number(value);
  else if (name === "visible" || name === "pirAnalytics" || name === "active" || name === "ropActive") inputValue = value === "true";
  else if (name === "obra") inputValue = value === "" ? null : value;
  else inputValue = value;

  // --- Lógica de Limpieza del Teléfono (Importada) ---
  if (name === "responsibleUser") {
      const trimmedValue = value.trim();
      // Limpiamos el valor de espacios, guiones y paréntesis, pero mantenemos un '+' inicial
      const cleanedValue = trimmedValue.replace(/[\s\-\(\)]/g, ''); 
      
      // Aseguramos que solo haya un '+' al inicio, si existe.
      let finalValue = cleanedValue;
      if (cleanedValue.startsWith('+')) {
        // Mantenemos el '+' inicial y luego removemos cualquier otro '+' del resto de la cadena.
        finalValue = '+' + cleanedValue.substring(1).replace(/\+/g, '');
      } else {
        // Si no empieza por '+', nos aseguramos de que no haya ninguno.
         finalValue = cleanedValue.replace(/\+/g, '');
      }
      
      // Finalmente, solo permitimos dígitos después del posible '+' inicial.
      finalValue = finalValue.replace(/[^\d+]/g, ''); // Remueve todo lo que no sea dígito o '+'
      inputValue = finalValue;
  }
  // ---------------------------------------------------

  if (name.includes(".")) {
    const [parent, child] = name.split(".");
    setFormData(prev => {
      const parentObject = prev[parent as keyof Camara];
      if (typeof parentObject === 'object' && parentObject !== null) {
        return {
          ...prev,
          [parent]: {
            ...(parentObject as object), 
            [child]: inputValue,
          },
        };
      }
      return prev;
    });
  } else {
    setFormData(prev => ({
      ...prev,
      [name]: inputValue,
    }));
  }
};

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    // --- Validación de Teléfono Aplicada ---
    if (formData.responsibleUser && !validatePhoneNumber(formData.responsibleUser)) {
      toast.error("Por favor, ingrese un número de teléfono válido para el responsable. Debe contener el código de área, solo dígitos, y opcionalmente puede incluir el '+' al inicio. Ejemplo: +5491112345678.");
      setLoading(false);
      return;
    }
    // ----------------------------------------

    try {
      console.log("Saving camara:", formData);
      if (formData.id && formData.id.trim() !== "") {
        //UPDATE
        const response = await updateCamaraById(formData.id, formData);
        console.log("updatecamara called with id:", formData.id);
      } else {
        // CREATE
        // await createcamara(formData);
        console.log("createcamara called");
      }

      toast.success(`Cámara ${formData.id ? 'editada' : 'guardada'} exitosamente`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setLoading(false);
      onClose();
    } catch (error) {
      const quotaMessage = getSlotQuotaMessage(error);
      if (quotaMessage) {
        toast.error(quotaMessage, {
          position: "top-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else if (error instanceof Error) {
        if (error.message === "Request failed with status code 409") {
          toast.error("La camara ya existe", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          console.error("Error al guardar la cámara:", error);
          toast.error("Error al guardar la cámara.", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } else {
        console.error("Error:", error);
      }
      setLoading(false);
    }
  };


  const [clearingExternal, setClearingExternal] = useState(false);

  const removeExternalChip = async () => {
    if (!formData.feedID) {
      toast.error("La cámara no tiene feedID, no se puede eliminar el chip externo.");
      return;
    }

    setClearingExternal(true);
    try {
      await clearExternalChipICCID(formData.feedID);
      toast.success("Chip Externo eliminado exitosamente");
      setFormData({
        ...formData,
        chipICCID: "",
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.message || "Error al eliminar el chip externo.");
      } else {
        console.error("Error al eliminar el chip externo:", error);
        toast.error("Error al eliminar el chip externo.");
      }
    } finally {
      setClearingExternal(false);
    }
  };

  const markAsExternal = () => {
    try {
      const response = markExternalChipICCID(formData.id);
      
        toast.success(`CHIP Externo seteado exitosamente`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setFormData({
          ...formData,
          chipICCID: "EXTERNO",
        });

      
      
    } catch (error) {
      console.error("Error al marcar la camara como externa:", error);
      toast.error("Error al marcar el chip de la camara como externo.", {
      })
      
    }
  };


  const handleSaveBillingType = async () => {
    if (!formData.identifier) {
      toast.error("La cámara no tiene identifier, no se puede cambiar la licencia.");
      return;
    }

    if (
      formData.billingType === "mensual" &&
      (!formData.renewalDay || formData.renewalDay < 1 || formData.renewalDay > 28)
    ) {
      toast.error("Ingrese un día de renovación válido (1 a 28) para la licencia mensual.");
      return;
    }

    setSavingBillingType(true);
    try {
      await updateCamaraBillingType(formData.identifier, formData.billingType, formData.renewalDay);
      toast.success("Tipo de licencia actualizado exitosamente");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.message || "Error al actualizar el tipo de licencia.");
      } else {
        console.error("Error al actualizar el tipo de licencia:", error);
        toast.error("Error al actualizar el tipo de licencia.");
      }
    } finally {
      setSavingBillingType(false);
    }
  };

  const handleMigrate = async () => {
    if (!formData.feedID || !formData.customerId) {
      toast.error("FeedID y CustomerID son requeridos para la migración.");
      return;
    }

    setMigrating(true);
    try {
      const response = await fetchRemainingImages(formData.feedID, formData.customerId);
      toast.success(response || "Proceso de migración encolado exitosamente.");
    } catch (error) {
      console.error("Error al migrar imágenes:", error);
      toast.error("Error al encolar la migración de imágenes.");
    } finally {
      setMigrating(false);
    }
  };


  const [markAsExternalDisabled, setMarkAsExternalDisabled] = useState(false);

  useEffect(() => {
    setMarkAsExternalDisabled(!!formData?.chipICCID);
  }, [formData?.chipICCID]);


  // --- Lógica de Validación de Teléfono ---
  const validatePhoneNumber = (phone: string): boolean => {
    // Expresión regular para el formato de teléfono (ej. +5491112345678 o 1112345678)
    // Permite un '+' opcional al inicio, seguido de 7 a 15 dígitos.
    const phoneRegex = /^\+?[0-9]{7,15}$/; 
    return phoneRegex.test(phone);
  };
  // ----------------------------------------------------


  if (!isOpen) return null;

  console.log(formData);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-12 z-50 overflow-y-auto">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl my-8 mx-4">
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-xl font-bold">{camara ? 'Editar Cámara' : 'Añadir Nueva Cámara'}</h2>
          <button className="text-gray-500 hover:text-gray-700 text-2xl" onClick={onCloseCancel}>&times;</button>
        </div>

        <div>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="Name" className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500">Nombre</label>
              <input type="text" id="Name" name="Name" value={formData.Name} onChange={handleInputChange} className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
            </div>

            {/*<div className="mb-4">
              <label htmlFor="identifier" className="block  text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500">PWD</label>
              <input type="text" id="identifier" name="identifier" value={formData.identifier} onChange={handleInputChange} className="w-full p-2 border bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" readOnly />
            </div>*/}

            <div className="mb-4">
              <label
                htmlFor="status"
                className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500"
              >
                Creado - Aprobado - Terminado (Archivado) - Suspendida (Baja)
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Seleccione un estado</option>
                <option value="Creado">Creado</option>
                <option value="Aprobado">Aprobado</option>
                <option value="Terminado">Terminado</option>
                <option value="Suspendida">Suspendida</option>
              </select>
            </div>

            <div className="mb-4">
              <label
                htmlFor="active"
                className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500"
              >
                Generar Timelapse Automáticamente
              </label>
              <select
                id="active"
                name="active"
                value={formData.active ? "true" : "false"}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>

            <div className="mb-4">
              <label
                htmlFor="pirAnalytics"
                className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500"
              >
                Analisis IA PIR
              </label>
              <select
                id="pirAnalytics"
                name="pirAnalytics"
                value={formData.pirAnalytics ? "true" : "false"}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                
                <option value="true">Si</option>
                <option value="false">No</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="ropActive" className="block text-gray-700 font-medium mb-1">
                ROP
              </label>
              <select
                id="ropActive"
                name="ropActive"
                value={formData.ropActive ? "true" : "false"}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Si</option>
                <option value="false">No</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="obra" className="block text-gray-700 font-medium mb-1">
                Obra
              </label>
              <select
                id="obra"
                name="obra"
                value={formData.obra || ""}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sin obra asignada</option>
                {obras.map((obra) => (
                  <option key={obra.id} value={obra.id}>{obra.nombre}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label
                htmlFor="billingType"
                className="block text-gray-700 font-medium mb-1"
              >
                Tipo de Licencia
              </label>
              <div className="flex items-stretch gap-2">
                <select
                  id="billingType"
                  name="billingType"
                  value={formData.billingType}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="mensual">Mensual</option>
                  <option value="paquete_dias">Paquete de días</option>
                </select>
                {formData.billingType === "mensual" && (
                  <input
                    type="number"
                    id="renewalDay"
                    name="renewalDay"
                    min={1}
                    max={28}
                    placeholder="Día (1-28)"
                    value={formData.renewalDay ?? ""}
                    onChange={handleInputChange}
                    className="w-32 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                )}
                <button
                  type="button"
                  onClick={handleSaveBillingType}
                  disabled={savingBillingType || !formData.identifier}
                  className="px-4 py-2 border rounded bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 whitespace-nowrap flex-shrink-0 disabled:bg-blue-300"
                >
                  {savingBillingType ? "Guardando..." : "Guardar Licencia"}
                </button>
              </div>
            </div>

            <div className="mb-4">
            {/* Etiqueta arriba */}
            <label htmlFor="feedID" className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500">
              FeedID
            </label>
            
            {/* Contenedor para el input y el botón (al lado del otro) */}
            <div className="flex items-stretch gap-2"> {/* Cambiado a items-stretch para que ambos elementos tengan la misma altura */}
              <input 
                type="text" 
                id="feedID" 
                name="feedID" 
                value={formData.feedID} 
                onChange={handleInputChange} 
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                required 
              />
              <button 
                type="button"
                onClick={handleMigrate}
                disabled={migrating || !formData.feedID}
                className="px-4 py-2 border rounded bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 whitespace-nowrap flex-shrink-0 disabled:bg-green-300"
              >
                {migrating ? "Migrando..." : "Migrar: Teleport a S3"}
              </button>
            </div>
          </div>

            <div className="mb-4">
              <label htmlFor="chipICCID" className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500">Chip ICCID</label>
              <div className="flex items-stretch"> 
              <input type="text" id="chipICCID" name="chipICCID" value={formData.chipICCID} onChange={handleInputChange} className="w-full p-2 border bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" readOnly />
            <button
            className="ml-2 px-4 py-2 border rounded bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 whitespace-nowrap flex-shrink-0"
            type='button'
            onClick={markAsExternal}
            disabled={markAsExternalDisabled}
            >
                Chip Externo
              </button>
              {formData.chipICCID === "EXTERNO" && (
                <button
                className="ml-2 px-4 py-2 border rounded bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 whitespace-nowrap flex-shrink-0 disabled:bg-red-300"
                type='button'
                onClick={removeExternalChip}
                disabled={clearingExternal}
                >
                  {clearingExternal ? "Eliminando..." : "Eliminar Externo"}
                </button>
              )}
              </div>
              
            </div>

            <div className="mb-4">
              <label htmlFor="camaraUID" className="block text-gray-700 font-medium mb-1">Cámara UID/SR</label>
              <input type="text" id="camaraUID" name="camaraUID" value={formData.camaraUID} onChange={handleInputChange} className="w-full p-2 border bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" readOnly />
            </div>

            {formData.billingType !== "mensual" && (
              <>
                <div className="mb-4">
                  <label htmlFor="totalDays" className="block text-gray-700 font-medium mb-1">Dias contratados</label>
                  <input type="number" id="totalDays" name="feedDays.totalDays" value={formData.feedDays.totalDays} onChange={handleInputChange} className="w-full p-2 border bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div className="mb-4">
                  <label htmlFor="daysLeft" className="block text-gray-700 font-medium mb-1">Dias restantes</label>
                  <input type="number" id="daysLeft" name="feedDays.daysLeft" value={formData.feedDays.daysLeft} onChange={handleInputChange} className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
              </>
            )}

            <div className="mb-4">
              <label htmlFor="orientation" className="block text-gray-700 font-medium mb-1">Orientación</label>
              <select id="orientation" name="orientation" value={formData.orientation} onChange={handleInputChange} className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Seleccione una orientación</option>
                {ORIENTATION_DEGREES.map(option => (
                  <option key={option} value={option}>{ORIENTATION_LABELS[option] || option}</option>
                ))}
              </select>
            </div>

            {/*<div className="mb-4">
              <label htmlFor="responsibleUser" className="block text-gray-700 font-medium mb-1">Número del responsable</label>
              
              <input 
                type="text" 
                id="responsibleUser" 
                name="responsibleUser" 
                value={formData.responsibleUser} 
                onChange={handleInputChange} 
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="Ej: +5491112345678" // Guía para el formato
              />
            </div>*/}

            <hr className="my-6 border-gray-200" />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                onClick={onCloseCancel}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SaveOrEditCamaras;
